<template>
  <nav
    id="nav-container"
    class="col-span-4 md:col-start-1 overflow-x-auto flex relative 2xl:overflow-x-visible no-scrollbar"
  >
    <ul id="menu" class="col-start-2 col-span-12 flex relative">
      <li
        v-if="activeIndex !== null"
        role="separator"
        class="absolute h-[4px] bg-orange"
        :style="underlineStyle"
      ></li>
      <li
        v-for="(link, index) in navlinks"
        :key="link.title"
        ref="menuLinks"
        :class="[
          'mr-10 transition-all duration-200 ease-in-out group',
          activeIndex === index ? 'text-typography' : 'text-typography-40',
        ]"
        :aria-current="activeIndex === index ? 'page' : undefined"
      >
        <navigation-button
          @setActiveIndex="setActiveIndex"
          :activeIdx="activeIndex"
          :label="link.title"
          :index="index"
          :lineWidth="widthPos"
          :posLeft="leftPos"
        />
      </li>
      <li
        role="separator"
        v-if="activeIndex !== null"
        class="absolute h-[4px] bg-orange bottom-0"
        :style="underlineStyle"
      ></li>
    </ul>
  </nav>
</template>

<script setup lang="ts">
import { ref, toRefs, onMounted, nextTick, computed, watch, watchEffect } from "vue";
import { IPageChild } from "@/types/types";
import NavigationButton from "@atoms/NavigationButton/NavigationButton.vue";

interface NavigationProps {
  navlinks: IPageChild[];
  activeIndex: number;
}

const menuLinks = ref<HTMLElement[]>();
const width = ref<number | null>(null);
const left = ref<number | null>(null);
const animationDuration = ref("0.3s");

const props = withDefaults(defineProps<NavigationProps>(), {});
const { navlinks, activeIndex } = toRefs(props);

type Emits = {
  (e: "setActiveIndex", index: number): void;
};
const emit = defineEmits<Emits>();

onMounted(async () => {
  if (menuLinks) {
    await nextTick(() => {
      setPosition(activeIndex.value);
    });
  }
});

function setActiveIndex(index: number) {
  setPosition(index);
  emit("setActiveIndex", index);
}

watchEffect(() => {
  // Watch for changes in the URL
  window.addEventListener("popstate", async () => {
    // Watch for changes in the URL caused by history.pushState()
    await nextTick(() => {
      setPosition(activeIndex.value);
    });
  });
});

function setPosition(index: number) {
  if (!menuLinks.value || !navlinks.value.length) return;

  const linkElement = menuLinks.value[index];
  const navElement = document.querySelector("#nav-container"); // Adjust the selector as needed to target your nav container

  if (linkElement && navElement) {
    const navWidth = navElement.clientWidth;
    const linkLeft = linkElement.offsetLeft;
    const linkWidth = linkElement.offsetWidth;

    // Calculate the scroll position to center the link, if possible
    const scrollTo = linkLeft - navWidth / 2 + linkWidth / 2;

    navElement.scrollTo({
      left: scrollTo,
      behavior: "smooth",
    });

    width.value = linkWidth;
    left.value = linkLeft;
  }
}
watch(activeIndex, (newIndex, oldIndex) => {
  const constantSpeed = 500;
  if (typeof newIndex === "number" && typeof oldIndex === "number") {
    const distance = Math.abs(newIndex - oldIndex); // 0, 1, or 2
    const duration = (distance * 100) / constantSpeed; // Assuming each step is 100px
    animationDuration.value = `${duration}s`;
  }
});

const leftPos = computed(() => {
  if (!left.value) return;
  return left.value;
});

const widthPos = computed(() => {
  if (!width.value) return;
  return width.value;
});

const underlineStyle = computed(() => {
  return {
    left: `${left.value}px`,
    width: `${width.value}px`,
    transition: `all ${animationDuration.value} ease-out`,
  };
});
</script>
